/**
 * Hook for managing LINE Bot integration
 * Provides methods to generate verification codes, check status, and unlink
 */

import { useState, useCallback } from 'react'

interface VerificationCodeResponse {
  code: string
  expiresAt: string
  expiresInMinutes: number
}

interface LineStatus {
  shortCode: string
  linked: boolean
  linkedGroupCount: number
  linkedGroupIds: string[]
}

interface UseLineIntegrationReturn {
  // State
  verificationCode: VerificationCodeResponse | null
  lineStatus: LineStatus | null
  isLoading: boolean
  error: string | null

  // Actions
  generateVerificationCode: (shortCode: string, venueId?: string) => Promise<VerificationCodeResponse | null>
  getLineStatus: (shortCode: string) => Promise<LineStatus | null>
  unlinkLine: (shortCode: string) => Promise<boolean>
  clearError: () => void
}

export function useLineIntegration(): UseLineIntegrationReturn {
  const [verificationCode, setVerificationCode] = useState<VerificationCodeResponse | null>(null)
  const [lineStatus, setLineStatus] = useState<LineStatus | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const generateVerificationCode = useCallback(async (
    shortCode: string,
    venueId?: string
  ): Promise<VerificationCodeResponse | null> => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch('/api/line/verification-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shortCode, venueId }),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to generate verification code')
      }

      const data = await response.json() as VerificationCodeResponse
      setVerificationCode(data)
      return data
    } catch (e: any) {
      setError(e.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  const getLineStatus = useCallback(async (shortCode: string): Promise<LineStatus | null> => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch(`/api/line/status/${shortCode}`)

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to get LINE status')
      }

      const data = await response.json() as LineStatus
      setLineStatus(data)
      return data
    } catch (e: any) {
      setError(e.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  const unlinkLine = useCallback(async (shortCode: string): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await fetch(`/api/line/link/${shortCode}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to unlink LINE Bot')
      }

      setLineStatus(null)
      setVerificationCode(null)
      return true
    } catch (e: any) {
      setError(e.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  const clearError = useCallback(() => setError(null), [])

  return {
    verificationCode,
    lineStatus,
    isLoading,
    error,
    generateVerificationCode,
    getLineStatus,
    unlinkLine,
    clearError,
  }
}
